import { createContext, useContext } from 'react';
import { sizedLength, splitStem } from '@/lib/stem';

/**
 * The stem's heading level: h1 on the game's own screens (the question is the page's heading). A page that shows a
 * question inside its own outline (the front page's practice round, 30 Oct 2026) sets a lower level around it.
 */
export const StemLevel = createContext<'h1' | 'h2' | 'h3'>('h1');

/**
 * A stem's words with the final question on its own line (13 Oct; `lib/stem.ts` decides where). Two block spans inside
 * whatever heading or paragraph holds it; the space between them keeps the text whole for screen readers and copying.
 */
export function StemText({ text }: { text: string }) {
  const { lead, ask } = splitStem(text);
  if (!lead) return <>{ask}</>;
  return <><span data-part="stem-lead" className="block">{lead}{' '}</span><span data-part="stem-ask" className="mt-[0.4em] block">{ask}</span></>;
}

/** The question stem. Typography lives here so every format sets it the same way; long stems step down a size. */
export function Stem({ text, className = '' }: { text: string; className?: string }) {
  const n = sizedLength(text);
  const len = n > 170 ? 'long' : n > 110 ? 'mid' : 'short';
  const size = len === 'long' ? 'text-[1.1875rem] leading-[1.32]' : len === 'mid' ? 'text-[1.3125rem] leading-[1.3]' : 'text-[1.5rem] leading-[1.25]';
  const Tag = useContext(StemLevel);
  // data-len lets the desktop question (formats.css) set each band a step larger.
  return <Tag data-len={len} className={`font-display font-bold tracking-[-0.015em] text-ink [text-wrap:pretty] ${size} ${className}`}><StemText text={text} /></Tag>;
}
